import React, { useEffect } from "react";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import axios from "axios";
import { FiEye } from "react-icons/fi"; // For view details icon
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast, { Toaster } from "react-hot-toast";

// Helper function to format date strings
const formatDate = (isoString) => {
  if (!isoString) return 'N/A';
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch (error) {
    console.error("Error formatting date:", isoString, error);
    return isoString; // Fallback to original string on error
  }
};

// Helper function for status badges
const getStatusBadgeClass = (status) => {
  if (!status) return "bg-light text-dark"; // Default for undefined/null
  switch (status.toLowerCase()) {
    case "delivered": return "bg-success";
    case "shipped": return "bg-info text-dark";
    case "paid": return "bg-primary";
    case "order received": return "bg-warning text-dark";
    case "canceled": return "bg-danger";
    case "pending": return "bg-warning text-dark";
    default: return "bg-secondary";
  }
};

// Helper function for delivery priority badges
const getPriorityBadgeClass = (priority) => {
  if (!priority) return "bg-light text-dark";
  switch (priority.toLowerCase()) {
    case "priority": return "bg-danger";
    case "standard": return "bg-info text-dark"; // Matched Orders.js, was bg-primary
    default: return "bg-secondary";
  }
};

// Helper to capitalize first letter of a string
const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

function Dashbord() {
  const token = sessionStorage.getItem("token");
  const queryClient = useQueryClient();
  const baseUrl = process.env.REACT_APP_APIURL;

  // Sidebar toggle effect (remains unchanged)
  useEffect(() => {
    const toggleButton = document.querySelector(".toggle-sidebar-btn");
    if (toggleButton) {
      const toggleSidebar = () => {
        document.body.classList.toggle("toggle-sidebar");
      };
      toggleButton.addEventListener("click", toggleSidebar);
      return () => {
        toggleButton.removeEventListener("click", toggleSidebar);
      };
    }
  }, []);

  // Fetch dashboard statistics using React Query
  const {
    data: dashboardStats,
    isLoading,
    isError,
    error: queryError,
  } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => {
      if (!token) {
        throw new Error("Authorization token not found. Please log in.");
      }
      const response = await axios.get(
        `${baseUrl}api/admin/dashboard-stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data && response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to fetch dashboard data.");
      }
    },
    enabled: !!token, // Only fetch if token exists
  });

  // Mutation for updating order status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ orderId, newStatus }) => {
      return axios.put(
        `${baseUrl}api/order/order-status/${orderId}`,
        { status: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    },
    onSuccess: (data, variables) => {
      toast.success(`Order status updated to "${variables.newStatus}"!`);
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to update status. Please try again.");
    },
  });

  // Mutation for toggling shop status
  const toggleShopMutation = useMutation({
    mutationFn: async (isOpen) => {
      if (!token) throw new Error("Token not found.");
      // Using the same endpoint as updateShopLocation
      return axios.put(
        `${baseUrl}api/shop`,
        { isOpen },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: () => {
      toast.success("Shop status updated!");
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to update shop status.");
    },
  });

  const handleStatusChange = (orderId, newStatus) => {
    if (newStatus) { // Ensure a status is actually selected
      updateStatusMutation.mutate({ orderId, newStatus });
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <>
        <Toaster position="top-right" reverseOrder={false} />
        <Topbar />
        <Sidebar />
        <main id="main" className="main">
          <div className="pagetitle"><h1>Dashboard</h1></div>
          <section className="section dashboard d-flex justify-content-center align-items-center" style={{ height: "calc(100vh - 180px)" }}>
            <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  // Error State
  if (isError) {
    return (
      <>
        <Toaster position="top-right" reverseOrder={false} />
        <Topbar />
        <Sidebar />
        <main id="main" className="main">
          <div className="pagetitle"><h1>Dashboard</h1></div>
          <section className="section dashboard">
            <div className="alert alert-danger" role="alert">
              <h4 className="alert-heading">Error Loading Dashboard!</h4>
              <p>{queryError?.message || "An unexpected error occurred."}</p>
              <hr />
              <p className="mb-0">Please try refreshing the page. If the problem persists, contact support.</p>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  // No Data State (or if dashboardStats is not populated as expected)
  if (!dashboardStats) {
    return (
      <>
        <Toaster position="top-right" reverseOrder={false} />
        <Topbar />
        <Sidebar />
        <main id="main" className="main">
          <div className="pagetitle"><h1>Dashboard</h1></div>
          <section className="section dashboard">
            <div className="alert alert-light text-center" role="alert">
              No dashboard data is currently available. This might be because there is no data to show or an issue with fetching it.
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  // Main Dashboard Content
  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      <Topbar />
      <Sidebar />
      <main id="main" className="main">
        <div className="pagetitle d-flex justify-content-between align-items-center">
          <div>
            <h1>Admin Dashboard</h1>
            <nav>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                <li className="breadcrumb-item active">Dashboard</li>
              </ol>
            </nav>
          </div>
          <div className="card shadow-sm mb-0">
            <div className="card-body py-2 d-flex align-items-center bg-light rounded">
              <span className="me-2 fw-bold text-secondary">Shop Status:</span>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="shopStatusToggle"
                  checked={!!dashboardStats?.shopStatus?.isOpen}
                  onChange={() => toggleShopMutation.mutate(!dashboardStats?.shopStatus?.isOpen)}
                  style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                  disabled={toggleShopMutation.isLoading}
                />
                <label
                  className={`form-check-label ms-2 fw-bold ${dashboardStats?.shopStatus?.isOpen ? 'text-success' : 'text-danger'}`}
                  htmlFor="shopStatusToggle"
                >
                  {dashboardStats?.shopStatus?.isOpen ? 'OPEN' : 'CLOSED'}
                </label>
              </div>
            </div>
          </div>
        </div>

        <section className="section dashboard">
          {/* Info Cards Row 1 */}
          <div className="row gy-4">
            <div className="col-xxl-4 col-md-6">
              <div className="card info-card sales-card shadow-sm h-100">
                <div className="card-body">
                  <h5 className="card-title">Today's Orders</h5>
                  <div className="d-flex align-items-center">
                    <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                      <i className="bi bi-cart-check"></i>
                    </div>
                    <div className="ps-3">
                      <h6>{dashboardStats.todayStats?.orders ?? 0}</h6>
                      <span className="text-muted small pt-1">orders placed today</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xxl-4 col-md-6">
              <div className="card info-card revenue-card shadow-sm h-100">
                <div className="card-body">
                  <h5 className="card-title">Today's Revenue</h5>
                  <div className="d-flex align-items-center">
                    <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                      <i className="bi bi-currency-pound"></i>
                    </div>
                    <div className="ps-3">
                      <h6>£{parseFloat(dashboardStats.todayStats?.revenue || 0).toFixed(2)}</h6>
                      <span className="text-muted small pt-1">earned today</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xxl-4 col-md-6">
              <div className="card info-card customers-card shadow-sm h-100">
                <div className="card-body">
                  <h5 className="card-title">Total Users</h5>
                  <div className="d-flex align-items-center">
                    <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                      <i className="bi bi-people"></i>
                    </div>
                    <div className="ps-3">
                      <h6>{dashboardStats.totalUsers ?? 0}</h6>
                      <span className="text-muted small pt-1">registered users</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Info Cards Row 2 (continuation) */}
            <div className="col-xxl-4 col-md-6">
              <div className="card info-card default-card-theme shadow-sm h-100">
                <div className="card-body">
                  <h5 className="card-title">Overall Revenue</h5>
                  <div className="d-flex align-items-center">
                    <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                      <i className="bi bi-cash-stack"></i>
                    </div>
                    <div className="ps-3">
                      <h6>£{parseFloat(dashboardStats.totalRevenue || 0).toFixed(2)}</h6>
                      <span className="text-muted small pt-1">total revenue generated</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xxl-4 col-md-6">
              <div className="card info-card default-card-theme shadow-sm h-100">
                <div className="card-body">
                  <h5 className="card-title">Overall Orders</h5>
                  <div className="d-flex align-items-center">
                    <div className="card-icon rounded-circle d-flex align-items-center justify-content-center">
                      <i className="bi bi-journal-check"></i>
                    </div>
                    <div className="ps-3">
                      <h6>{dashboardStats.totalOrders ?? 0}</h6>
                      <span className="text-muted small pt-1">total orders placed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xxl-4 col-md-6">
              <div className="card info-card default-card-theme shadow-sm h-100">
                <div className="card-body">
                  <h5 className="card-title">Order Status Summary</h5>
                  {dashboardStats.ordersByStatus && Object.keys(dashboardStats.ordersByStatus).length > 0 ? (
                    <ul className="list-group list-group-flush pt-2">
                      {Object.entries(dashboardStats.ordersByStatus).map(([status, count]) => (
                        <li key={status} className="list-group-item d-flex justify-content-between align-items-center px-0 py-1">
                          {capitalizeFirstLetter(status)}
                          <span className={`badge rounded-pill px-2 py-1 ${getStatusBadgeClass(status)}`}>{count}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted small pt-2">No order status data available.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Orders Table */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="card recent-data-card shadow-sm">
                <div className="card-body">
                  <h5 className="card-title">Recent Orders <span>({dashboardStats.recentOrders?.length ?? 0} latest)</span></h5>
                  {dashboardStats.recentOrders && dashboardStats.recentOrders.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-striped table-hover table-bordered align-middle">
                        <thead className="table-light">
                          <tr>
                            <th scope="col" className="text-center">#</th>
                            <th scope="col">Order ID</th>
                            <th scope="col">User Name</th>
                            <th scope="col" className="text-end">Amount</th>
                            <th scope="col" className="text-center">Status</th>
                            <th scope="col" className="text-center">Priority</th>
                            <th scope="col" className="text-center">Shift</th>
                            <th scope="col" className="text-center">Order Date</th>
                            <th scope="col" className="text-center" style={{ minWidth: "180px" }}>Change Status</th> {/* New Header */}
                            <th scope="col" className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardStats.recentOrders.map((order, index) => (
                            <tr key={order._id}>
                              <td className="text-center">{index + 1}</td>
                              <td>{order.orderId}</td>
                              <td className="text-nowrap">{order.userName || 'N/A'}</td>
                              <td className="text-end fw-semibold">£{parseFloat(order.payableAmount || 0).toFixed(2)}</td>
                              <td className="text-center">
                                <span className={`badge rounded-pill px-2 py-1 ${getStatusBadgeClass(order.status)}`}>
                                  {capitalizeFirstLetter(order.status)}
                                </span>
                              </td>
                              <td className="text-center">
                                <span
                                  className={`badge rounded-pill px-2 py-1 ${getPriorityBadgeClass(order.deliveryPriority)} ${order.deliveryPriority?.toLowerCase() === 'priority' ? 'badge-pulsate' : ''}`}
                                >
                                  {capitalizeFirstLetter(order.deliveryPriority) || 'N/A'}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-light text-dark border">
                                  {order.shiftName || 'N/A'}
                                </span>
                              </td>
                              <td className="text-center text-nowrap">{formatDate(order.orderDate)}</td>
                              <td className="text-center"> {/* New Cell for Status Change */}
                                <div className="d-flex align-items-center justify-content-center">
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ maxWidth: "130px" }}
                                    defaultValue={order.status || ''} // Controlled component
                                    onChange={(e) =>
                                      handleStatusChange(order._id, e.target.value)
                                    }
                                    disabled={updateStatusMutation.isLoading && updateStatusMutation.variables?.orderId === order._id}
                                  >
                                    <option value="" disabled>Set Status</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Order Received">Order Received</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Shipped">Shipped</option>
                                    <option value="Delivered">Delivered</option>
                                    <option value="Canceled">Canceled</option>
                                  </select>
                                  {updateStatusMutation.isLoading && updateStatusMutation.variables?.orderId === order._id &&
                                    <div className="spinner-border spinner-border-sm text-primary ms-2" role="status">
                                      <span className="visually-hidden">...</span>
                                    </div>
                                  }
                                </div>
                              </td>
                              <td className="text-center">
                                <Link
                                  to={`/order-details/${order._id}`}
                                  className="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                                  title="View Order Details"
                                >
                                  <FiEye size={14} className="me-1" /> View
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="alert alert-light text-center py-3" role="alert">
                      No recent orders to display.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Users Table (remains unchanged) */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="card recent-data-card shadow-sm">
                <div className="card-body">
                  <h5 className="card-title">Recently Joined Users <span>({dashboardStats.recentUsers?.length ?? 0} latest)</span></h5>
                  {dashboardStats.recentUsers && dashboardStats.recentUsers.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-striped table-hover table-bordered align-middle">
                        <thead className="table-light">
                          <tr>
                            <th scope="col" className="text-center">#</th>
                            <th scope="col">Full Name</th>
                            <th scope="col">Email</th>
                            <th scope="col">Phone</th>
                            <th scope="col" className="text-center">Joined At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardStats.recentUsers.map((user, index) => (
                            <tr key={user._id || user.email + '-' + index}> {/* Use user._id if available */}
                              <td className="text-center">{index + 1}</td>
                              <td>{user.fullname || 'N/A'}</td>
                              <td>{user.email || 'N/A'}</td>
                              <td className="text-nowrap">{user.phone || 'N/A'}</td>
                              <td className="text-center text-nowrap">{formatDate(user.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="alert alert-light text-center py-3" role="alert">
                      No recently joined users to display.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default Dashbord;